/**
 * FBC updates for reviewers — decision 0007 (Bettina, 2026-11-02).
 * UnreadFbcUpdates: the dashboard section, newest first, until marked read.
 * CaseFbcUpdates: one case's FBC updates (case page tab); viewing marks them read.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../lib/api';

interface Update {
  id: number; case_id?: number; by: string | null; from_state: string; to_state: string | null;
  note: string; created_at: string; read?: boolean; servicing_af?: string; territory_af?: string | null;
}

const LABEL: Record<string, string> = { NOTIFIED: 'Notified', ACKD: 'Acknowledged', CONFIRMED_VIOLATION: 'Confirmed violation', RESOLVED: 'Resolved' };
const stamp = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const what = (u: Update) => (u.to_state ? `${LABEL[u.from_state] ?? u.from_state} → ${LABEL[u.to_state] ?? u.to_state}` : 'Note');

export function UnreadFbcUpdates({ onOpenCase }: { onOpenCase: (caseId: string) => void }) {
  const [rows, setRows] = useState<Update[] | null>(null);
  const load = () => api<{ updates: Update[] }>('/api/fbc/updates?unread=1').then((d) => setRows(d.updates)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || rows.length === 0) return null;

  const read = async (id: number) => { await api(`/api/fbc/updates/${id}/read`, { method: 'POST' }); load(); };
  const readAll = async () => { await api('/api/fbc/updates/read-all', { method: 'POST' }); load(); };

  return (
    <div className="card card-pad" style={{ borderColor: 'color-mix(in oklch, var(--accent) 40%, var(--border))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5 }}>
          <Icon name="bell" size={14} /> {rows.length} unread FBC update{rows.length === 1 ? '' : 's'}
        </div>
        <button className="btn btn-sm btn-ghost" onClick={readAll}><Icon name="check" size={12} /> Mark all read</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {rows.map((u) => (
          <div key={u.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div>
                <a href="#" onClick={(e) => { e.preventDefault(); onOpenCase(`CASE-${u.case_id}`); }} className="mono">Case #{u.case_id}</a>
                <span className="muted"> · {u.servicing_af} → {u.territory_af ?? '—'} · {u.by ?? 'FBC'} · {stamp(u.created_at)}</span>
                <span style={{ fontWeight: 600 }}> · {what(u)}</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{u.note}</div>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => read(u.id)} title="Mark read">Mark read</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CaseFbcUpdates({ caseId }: { caseId: string }) {
  const num = caseId.replace(/^CASE-/, '').replace(/^0+/, '');
  const [rows, setRows] = useState<Update[] | null>(null);
  useEffect(() => {
    api<{ updates: Update[] }>(`/api/fbc/updates/case/${num}`)
      .then((d) => { setRows(d.updates); if (d.updates.some((u) => !u.read)) void api(`/api/fbc/updates/case/${num}/read`, { method: 'POST' }); })
      .catch(() => setRows([]));
  }, [num]);
  if (!rows) return <div className="muted" style={{ fontSize: 13 }}>Loading…</div>;
  if (rows.length === 0) return <div className="muted" style={{ fontSize: 13 }}>No FBC updates on this case yet. Once a notice goes out the case is assigned to the servicing partner's FBC, whose updates appear here.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 820 }}>
      {rows.map((u) => (
        <div key={u.id} className="card card-pad" style={{ fontSize: 13 }}>
          <div className="muted" style={{ fontSize: 12 }}>{stamp(u.created_at)} · {u.by ?? 'FBC'} · <span style={{ fontWeight: 600, color: 'var(--text)' }}>{what(u)}</span>{u.read === false ? <span className="c-warn"> · new</span> : null}</div>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{u.note}</div>
        </div>
      ))}
    </div>
  );
}
